import React from 'react'
import { useTranslation } from 'react-i18next'

import { useAPIGetReservationRulesAndTerms } from 'api/apiHooks/useAPIGetReservationRulesAndTerms'

export type ReservationRulesAndTermsLink = { href: string; label: string }

type LinkWithType = {
  href: string
  label: string
  attachmentType: string | null
}

const pairLinks = (items: LinkWithType[]): ReservationRulesAndTermsLink[] => {
  const rulesItems = items.filter((item) =>
    item.attachmentType?.endsWith('_rules')
  )
  const termsItems = items.filter((item) =>
    item.attachmentType?.endsWith('_terms')
  )
  const otherItems = items.filter(
    (item) =>
      !item.attachmentType?.endsWith('_rules') &&
      !item.attachmentType?.endsWith('_terms')
  )

  const paired: ReservationRulesAndTermsLink[] = []
  const pairCount = Math.max(rulesItems.length, termsItems.length)
  for (let i = 0; i < pairCount; i++) {
    const rulesItem = rulesItems[i]
    const termsItem = termsItems[i]
    if (rulesItem) paired.push({ href: rulesItem.href, label: rulesItem.label })
    if (termsItem) paired.push({ href: termsItem.href, label: termsItem.label })
  }

  return [
    ...paired,
    ...otherItems.map((item) => ({ href: item.href, label: item.label })),
  ]
}

interface Props {
  contractId: string
  reservableCategory: string | null
  realPropertyHierarchyId: number | null
  fallbackLinks: ReservationRulesAndTermsLink[]
}

export const ReservationRulesAndTermsLinks: React.FC<Props> = ({
  contractId,
  reservableCategory,
  realPropertyHierarchyId,
  fallbackLinks,
}) => {
  const { i18n } = useTranslation()
  const { data: rulesAndTerms } = useAPIGetReservationRulesAndTerms(
    contractId,
    reservableCategory,
    i18n.language,
    realPropertyHierarchyId
  )

  const dbItems: LinkWithType[] = rulesAndTerms
    ? [
        ...rulesAndTerms.files.map((file) => ({
          href: file.url,
          label: file.displayFileName,
          attachmentType: file.attachmentType,
        })),
        ...rulesAndTerms.externalLinks.map((link) => ({
          href: link.url,
          label: link.title,
          attachmentType: link.attachmentType,
        })),
      ]
    : []

  const links = dbItems.length > 0 ? pairLinks(dbItems) : fallbackLinks

  if (links.length === 0) return null

  return (
    <div className="space-y-2">
      {links.map((link) => (
        <a
          key={link.href}
          href={link.href}
          className="block text-primary hover:text-link-hover underline text-sm"
          target="_blank"
          rel="noreferrer"
        >
          {link.label}
        </a>
      ))}
    </div>
  )
}
